import React, { useState } from "react";
import { motion } from "framer-motion";

export default function LoanCalculator() {
  const [vehicleCost, setVehicleCost] = useState("");
  const [downPayment, setDownPayment] = useState("");
  const [interestRate, setInterestRate] = useState("");
  const [term, setTerm] = useState("");
  const [termType, setTermType] = useState("years"); // 👈 NEW
  const [paymentPeriod, setPaymentPeriod] = useState("monthly");
  const [monthlyPayment, setMonthlyPayment] = useState(null);

  // FIXED payment calculation
  const calculatePayment = (e) => {
    e.preventDefault();

    const price = parseFloat(vehicleCost) || 0;
    const down = parseFloat(downPayment) || 0;
    const rate = parseFloat(interestRate) || 0;
    const termValue = parseFloat(term) || 0;

    if (price <= 0 || termValue <= 0 || rate <= 0) {
      setMonthlyPayment(null);
      return;
    }

    const P = price - down;               // Loan amount
    const r = rate / 100 / 12;            // Monthly interest rate
    
    // 👇 NEW: Correctly handle Years or Months
    const n = termType === "years" ? termValue * 12 : termValue;

    // Standard Amortization Formula
    const M = (P * r) / (1 - Math.pow(1 + r, -n));
    let calculated = M;

    // Convert based on payment period
    if (paymentPeriod === "bi-weekly") {
      calculated = (M * 12) / 26;
    } else if (paymentPeriod === "weekly") {
      calculated = (M * 12) / 52;
    }

    setMonthlyPayment(calculated.toFixed(2));
  };

  return (
    <section
      className="min-h-screen py-20 px-6 md:px-16 relative text-white"
      style={{
        background: "linear-gradient(135deg, #0a2545 0%, #0d5e63 100%)",
      }}
    >
      <div className="max-w-5xl mx-auto relative z-10">

        {/* Header */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.6 }}
          className="text-center mb-12"
        >
          <h5 className="uppercase text-teal-300 tracking-widest font-semibold mb-3">
            Car Loan Repayment Calculator
          </h5>
          <h1 className="text-5xl font-extrabold text-white mb-4">
            Work Out Your <span className="text-teal-300">Repayments</span>
          </h1>
          <p className="text-gray-300 max-w-2xl mx-auto leading-relaxed">
            Enter your vehicle details to calculate estimated loan payments.
            This tool helps you plan your financing with confidence.
          </p>
        </motion.div>

        {/* Calculator Form */}
        <motion.form
          onSubmit={calculatePayment}
          initial={{ opacity: 0, y: 30 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.2, duration: 0.8 }}
          className="bg-white/10 backdrop-blur-xl rounded-3xl p-8 shadow-2xl border border-teal-400/20"
        >
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">

            <div>
              <label className="block text-teal-200 mb-2">Vehicle Cost ($)</label>
              <input
                type="number"
                value={vehicleCost}
                onChange={(e) => setVehicleCost(e.target.value)}
                className="w-full bg-white/10 border border-teal-300/40 rounded-xl px-4 py-3 text-white placeholder-gray-400"
                placeholder="Enter vehicle cost"
              />
            </div>

            <div>
              <label className="block text-teal-200 mb-2">Down Payment ($)</label>
              <input
                type="number"
                value={downPayment}
                onChange={(e) => setDownPayment(e.target.value)}
                className="w-full bg-white/10 border border-teal-300/40 rounded-xl px-4 py-3 text-white placeholder-gray-400"
                placeholder="Enter down payment"
              />
            </div>

            <div>
              <label className="block text-teal-200 mb-2">Interest Rate (%)</label>
              <input
                type="number"
                value={interestRate}
                onChange={(e) => setInterestRate(e.target.value)}
                className="w-full bg-white/10 border border-teal-300/40 rounded-xl px-4 py-3 text-white placeholder-gray-400"
                placeholder="Enter interest rate"
              />
            </div>

            {/* UPDATED FIELD: Term + Term Type Toggle */}
            <div>
              <label className="block text-teal-200 mb-2">Payment Term</label>
              <div className="flex gap-4">
                <input
                  type="number"
                  value={term}
                  onChange={(e) => setTerm(e.target.value)}
                  className="w-full bg-white/10 border border-teal-300/40 rounded-xl px-4 py-3 text-white placeholder-gray-400"
                  placeholder="Enter term"
                />

                <select
                  value={termType}
                  onChange={(e) => setTermType(e.target.value)}
                  className="bg-white/10 border border-teal-300/40 rounded-xl px-4 py-3 text-white"
                >
                  <option value="years" className="text-black">Years</option>
                  <option value="months" className="text-black">Months</option>
                </select>
              </div>
            </div>

          </div>

          {/* Payment Period Dropdown */}
          <div className="mt-6">
            <label className="block text-teal-200 mb-2">Payment Period</label>
            <select
              value={paymentPeriod}
              onChange={(e) => setPaymentPeriod(e.target.value)}
              className="w-full bg-white/10 border border-teal-300/40 rounded-xl px-4 py-3 text-white"
            >
              <option value="monthly" className="text-black">Monthly</option>
              <option value="bi-weekly" className="text-black">Bi-Weekly</option>
              <option value="weekly" className="text-black">Weekly</option>
            </select>
          </div>

          {/* Calculate Button */}
          <div className="mt-10 flex flex-col md:flex-row items-center justify-between gap-6">
            <button
              type="submit"
              className="bg-teal-400 hover:bg-teal-500 text-[#0a2545] font-semibold py-3 px-10 rounded-full transition-all text-lg shadow-lg"
            >
              Calculate Payments
            </button>

            <div className="text-center md:text-right">
              <h4 className="text-lg font-semibold">Estimated Payment:</h4>
              <p className="text-2xl font-bold text-teal-300 mt-1">
                {monthlyPayment ? `$${monthlyPayment}` : "—"}
              </p>
            </div>
          </div>
        </motion.form>

        {/* CTA Section */}
        <motion.div
          initial={{ opacity: 0, y: 40 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.3, duration: 0.8 }}
          className="mt-16 text-center"
        >
          <h3 className="text-2xl font-semibold mb-4">
            Ready to take the next step?
          </h3>
          <p className="text-gray-300 mb-8">
            Click below to apply for your personalized car financing today.
          </p>
          <a
            href="/"
            className="bg-teal-400 hover:bg-teal-500 text-[#0a2545] font-bold px-10 py-3 rounded-full transition-all shadow-lg"
          >
            Apply Now
          </a>

          <p className="text-gray-400 text-sm mt-10 max-w-3xl mx-auto italic">
            Disclaimer: This calculator provides estimates for illustrative purposes only.
            Actual interest rates and payments may vary based on factors such as credit check,
            vehicle age, and loan approval terms. Taxes and additional fees are not included.
          </p>
        </motion.div>

      </div>
    </section>
  );
}
